import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus, Trash2, ShoppingBag, X, ArrowRight, Truck } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { api } from "../../lib/api";
import { fcfa, PLACEHOLDER_IMG } from "../../lib/format";

const PURPLE = "#5B2A9D";

export function CartDrawer() {
  const { items, updateQty, removeItem, total, isOpen, closeCart } = useCart();
  const navigate = useNavigate();
  const [threshold, setThreshold] = useState(0);

  useEffect(() => {
    if (isOpen && !threshold) {
      api.get("/settings/delivery").then(({ data }) => setThreshold(data?.free_delivery_threshold || 0)).catch(() => {});
    }
  }, [isOpen, threshold]);

  const goCheckout = () => { closeCart(); navigate("/commander"); };
  const remaining = threshold - total;
  const freeUnlocked = threshold > 0 && total >= threshold;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={closeCart}
            data-testid="cart-drawer-overlay"
            className="fixed inset-0 z-[70] bg-black/45"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3, ease: "easeInOut" }}
            data-testid="cart-drawer"
            className="fixed top-0 right-0 z-[71] h-full w-full max-w-md bg-brand-cream flex flex-col shadow-2xl"
          >
            <div className="flex items-center justify-between px-5 h-16 text-white shrink-0" style={{ background: PURPLE }}>
              <div className="flex items-center gap-2 font-heading font-bold text-lg">
                <ShoppingBag className="w-5 h-5" /> Mon panier
              </div>
              <button onClick={closeCart} data-testid="cart-drawer-close" className="p-2 rounded-full hover:bg-white/10">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-20">
                  <ShoppingBag className="w-12 h-12 text-brand-muted mb-4" />
                  <p className="text-brand-muted">Votre panier est vide.</p>
                  <button onClick={closeCart} data-testid="cart-drawer-continue" className="mt-6 rounded-full px-6 py-3 font-semibold text-white" style={{ background: PURPLE }}>
                    Continuer mes achats
                  </button>
                </div>
              ) : (
                items.map((item) => (
                  <div key={item.product_id} data-testid={`cart-drawer-item-${item.product_id}`} className="bg-white border border-brand-line rounded-2xl p-3 flex items-center gap-3">
                    <img src={item.image_url || PLACEHOLDER_IMG} alt={item.name} className="w-14 h-14 rounded-xl object-contain bg-brand-cream p-1 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-semibold text-sm text-brand-ink truncate">{item.name}</h3>
                      <p className="text-xs text-brand-muted">{fcfa(item.price)}</p>
                      <div className="flex items-center border border-brand-line rounded-full w-fit mt-1.5">
                        <button onClick={() => updateQty(item.product_id, item.qty - 1)} data-testid={`cart-drawer-minus-${item.product_id}`} className="p-1.5 hover:bg-brand-cream rounded-full"><Minus className="w-3 h-3" /></button>
                        <span className="w-7 text-center text-sm font-semibold">{item.qty}</span>
                        <button onClick={() => updateQty(item.product_id, item.qty + 1)} data-testid={`cart-drawer-plus-${item.product_id}`} className="p-1.5 hover:bg-brand-cream rounded-full"><Plus className="w-3 h-3" /></button>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-semibold text-sm text-brand-ink">{fcfa(item.price * item.qty)}</p>
                      <button onClick={() => removeItem(item.product_id)} data-testid={`cart-drawer-remove-${item.product_id}`} className="mt-2 p-1.5 text-brand-secondary hover:bg-red-50 rounded-full inline-flex"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-brand-line bg-white px-5 py-4 shrink-0 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-brand-sub">Total</span>
                  <span data-testid="cart-drawer-total" className="font-heading font-black text-2xl text-brand-ink">{fcfa(total)}</span>
                </div>
                <button onClick={goCheckout} data-testid="cart-drawer-checkout" className="w-full text-white hover:brightness-110 transition-all rounded-full px-6 py-3.5 font-bold flex items-center justify-center gap-2" style={{ background: PURPLE }}>
                  Commander <ArrowRight className="w-5 h-5" />
                </button>
                <button onClick={closeCart} className="w-full rounded-full px-6 py-2.5 text-sm font-medium text-brand-sub hover:bg-brand-cream">
                  Continuer mes achats
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
